<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
			<WSubTitle text="Plant & system status" />
			<div class="flex gap-1 flex-wrap">
				<PButton
					v-if="canFlush && (telemetry?.pending || 0) > 0"
					:loading="busyAction === 'flush'"
					icon="pi pi-send"
					label="Flush telemetry"
					outlined
					rounded
					size="small"
					@click="$emit('flush-telemetry')"
				/>
				<PButton
					v-if="canScheduler && scheduler?.running && !scheduler?.paused"
					:loading="busyAction === 'pause'"
					icon="pi pi-pause"
					label="Pause"
					outlined
					rounded
					size="small"
					@click="$emit('scheduler', 'pause')"
				/>
				<PButton
					v-if="canScheduler && scheduler?.running && scheduler?.paused"
					:loading="busyAction === 'resume'"
					icon="pi pi-play"
					label="Resume"
					outlined
					rounded
					size="small"
					@click="$emit('scheduler', 'resume')"
				/>
				<PButton
					v-if="canScheduler && scheduler?.running"
					:loading="busyAction === 'trigger_hourly'"
					icon="pi pi-clock"
					label="Trigger hourly"
					outlined
					rounded
					size="small"
					@click="$emit('scheduler', 'trigger_hourly')"
				/>
			</div>
		</div>

		<div v-if="dcs" class="dcs-strip mb-3">
			<div class="dcs-item">
				<span class="kpi-label">Ambient</span>
				<span class="kpi-value text-sm">{{ fmt(dcs.ambient_temp) }} °C</span>
			</div>
			<div class="dcs-item">
				<span class="kpi-label">Humidity</span>
				<span class="kpi-value text-sm">{{ fmt(dcs.humidity) }} %</span>
			</div>
			<div class="dcs-item">
				<span class="kpi-label">Irradiance</span>
				<span class="kpi-value text-sm">{{ fmt(dcs.irradiance) }} W/m²</span>
			</div>
			<div class="dcs-item">
				<span class="kpi-label">Wind</span>
				<span class="kpi-value text-sm">{{ fmt(dcs.wind_speed) }} m/s</span>
			</div>
			<div v-if="dcs.ts" class="dcs-item">
				<span class="kpi-label">DCS ts</span>
				<span class="kpi-value text-sm">{{ dcs.ts }}</span>
			</div>
		</div>

		<div class="ops-grid">
			<div v-if="health" class="ops-block">
				<div class="section-label mb-2">Subsystems</div>
				<div class="comp-list">
					<div
						v-for="c in health.components"
						:key="c.key"
						class="comp-row"
					>
						<span
							class="comp-dot"
							:class="c.effective ? 'on' : 'off'"
						/>
						<span>{{ c.label }}</span>
						<span
							v-if="c.simulated_offline"
							class="text-xs text-orange-500 ml-auto"
						>sim offline</span>
					</div>
				</div>
			</div>

			<div v-if="mda" class="ops-block">
				<div class="section-label mb-2">MDA ingest</div>
				<div class="ops-metric">
					<div class="kpi-label">Last sample</div>
					<div class="kpi-value text-base">{{ mda.last_ingest_at || '—' }}</div>
				</div>
				<div class="ops-metric mt-2">
					<div class="kpi-label">Age</div>
					<div class="kpi-value text-base" :class="ageClass">{{ ageLabel }}</div>
				</div>
				<div class="ops-metric mt-2">
					<div class="kpi-label">Samples (24h)</div>
					<div class="kpi-value text-base">{{ mda.samples_last_24h.toLocaleString() }}</div>
				</div>
			</div>

			<div v-if="telemetry" class="ops-block">
				<div class="section-label mb-2">CSS telemetry</div>
				<div class="ops-metric">
					<div class="kpi-label">Pending / failed / sent</div>
					<div class="kpi-value text-base">
						{{ telemetry.pending }} / {{ telemetry.failed }} / {{ telemetry.sent }}
					</div>
				</div>
				<div class="ops-metric mt-2">
					<div class="kpi-label">Dispatcher</div>
					<div class="kpi-value text-base">
						<PTag :severity="telemetry.dispatcher_running ? 'success' : 'secondary'">
							{{ telemetry.dispatcher_running ? 'running' : 'stopped' }}
						</PTag>
					</div>
				</div>
			</div>

			<div v-if="scheduler" class="ops-block">
				<div class="section-label mb-2">Scheduler</div>
				<div class="ops-metric">
					<div class="kpi-label">State</div>
					<div class="kpi-value text-base">
						<PTag v-if="!scheduler.running" severity="secondary">disabled</PTag>
						<PTag v-else-if="scheduler.paused" severity="warning">paused</PTag>
						<PTag v-else severity="success">running</PTag>
					</div>
				</div>
				<div v-if="scheduler.last_processed_hour" class="ops-metric mt-2">
					<div class="kpi-label">Last processed hour</div>
					<div class="kpi-value text-base">{{ scheduler.last_processed_hour }}</div>
				</div>
				<div v-if="scheduler.reason" class="text-sm text-color-secondary mt-2">
					{{ scheduler.reason }}
				</div>
			</div>
		</div>

		<div v-if="auditRecent?.length" class="mt-3">
			<div class="section-label mb-2">Recent audit activity</div>
			<ul class="audit-list">
				<li v-for="(line, idx) in auditRecent.slice().reverse()" :key="idx">{{ line }}</li>
			</ul>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import store from '@/store'
	import { DashboardPayload } from '@/brokers/DashboardBroker'

	type Health = NonNullable<DashboardPayload['health']>
	type Mda = NonNullable<DashboardPayload['mda']>
	type Telemetry = NonNullable<DashboardPayload['telemetry']>
	type Scheduler = NonNullable<DashboardPayload['scheduler']>
	type Dcs = NonNullable<DashboardPayload['dcs']>

	export default defineComponent({
		name: 'DashOpsPanel',
		props: {
			health: { type: Object as PropType<Health | null>, default: null },
			mda: { type: Object as PropType<Mda | null>, default: null },
			telemetry: { type: Object as PropType<Telemetry | null>, default: null },
			scheduler: { type: Object as PropType<Scheduler | null>, default: null },
			dcs: { type: Object as PropType<Dcs | null>, default: null },
			auditRecent: { type: Array as PropType<string[] | null>, default: null },
			busyAction: { type: String, default: '' },
		},
		emits: ['flush-telemetry', 'scheduler'],
		computed: {
			canFlush(): boolean {
				return store.getters.can('telemetry', 'manage')
			},
			canScheduler(): boolean {
				return store.getters.can('scheduler', 'manage')
			},
			ageLabel(): string {
				const s = this.mda?.age_seconds
				if (s == null) return '—'
				if (s < 60) return `${s}s ago`
				if (s < 3600) return `${Math.floor(s / 60)}m ago`
				if (s < 86400) return `${Math.floor(s / 3600)}h ago`
				return `${Math.floor(s / 86400)}d ago`
			},
			ageClass(): string {
				const s = this.mda?.age_seconds
				if (s == null) return ''
				if (s > 600) return 'text-orange-500'
				if (s > 120) return 'text-yellow-600'
				return 'text-green-500'
			},
		},
		methods: {
			fmt(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })
			},
		},
	})
</script>

<style scoped>
	.dcs-strip {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
		gap: 0.5rem;
		padding: 0.65rem 0.75rem;
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		background: var(--surface-ground);
	}
	.dcs-item {
		display: flex;
		flex-direction: column;
		gap: 0.15rem;
	}
	.ops-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 0.75rem;
	}
	.ops-block {
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		padding: 0.75rem 1rem;
		background: var(--surface-ground);
	}
	.section-label {
		font-size: 0.85rem;
		font-weight: 600;
	}
	.comp-list {
		display: flex;
		flex-direction: column;
		gap: 0.35rem;
		font-size: 0.875rem;
	}
	.comp-row {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
	.comp-dot {
		width: 0.55rem;
		height: 0.55rem;
		border-radius: 50%;
		flex-shrink: 0;
	}
	.comp-dot.on { background: #22c55e; }
	.comp-dot.off { background: #ef4444; }
	.kpi-label {
		font-size: 0.75rem;
		color: var(--text-color-secondary);
	}
	.kpi-value { font-weight: 600; }
	.audit-list {
		margin: 0;
		padding-left: 1rem;
		font-size: 0.75rem;
		color: var(--text-color-secondary);
		max-height: 8rem;
		overflow: auto;
	}
	.audit-list li + li { margin-top: 0.15rem; }
</style>
